var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';
import { ChatMessageDeliveryStatus, } from './types';
import { useStoreActions } from 'common/store/use-store-actions';
import { useProfileStore } from 'common/services/profile-service/service-store/service-store';
import { useChatMessagesStore } from './service-store/service-store';
import { useChatsListStore } from '../chats-list-service/service-store/service-store';
import { deleteChatMessageApi, editChatMessageApi, markChatMessagesAsReadApi, publishChatMessageApi } from './api';
import { chatMessagesQueryKeys, chatsListQueryKeys } from '../api-tags';
export var useChatMessagesActions = function () {
    var actions = useStoreActions(useChatMessagesStore);
    var queryClient = useQueryClient();
    var invalidateLatestChatMessages = useCallback(function (chatId) {
        return queryClient.invalidateQueries({
            queryKey: chatMessagesQueryKeys.byIdParams(chatId, {
                beforeSentAt: null,
                pageSize: useChatMessagesStore.getState().pageSize,
            }),
            exact: true,
            refetchType: 'none',
        });
    }, [queryClient]);
    return useMemo(function () { return (__assign(__assign({}, actions), { invalidateLatestChatMessagesAction: invalidateLatestChatMessages, publishChatMessageAction: function (_a) {
            var _b, _c;
            var chatId = _a.chatId, textMessage = _a.textMessage, localMessageId = _a.localMessageId, successCb = _a.successCb, errorCb = _a.errorCb;
            var author = useProfileStore.getState().content;
            var failedMessage = useChatMessagesStore
                .getState()
                .content.find(function (message) {
                return 'deliveryStatus' in message &&
                    message.deliveryStatus === ChatMessageDeliveryStatus.Failed &&
                    message.id === localMessageId &&
                    message.author.id === (author === null || author === void 0 ? void 0 : author.id);
            });
            var localId = (_b = failedMessage === null || failedMessage === void 0 ? void 0 : failedMessage.id) !== null && _b !== void 0 ? _b : uuid();
            if (author) {
                actions.setLocalMessage({
                    id: localId,
                    text: textMessage,
                    sentAt: (_c = failedMessage === null || failedMessage === void 0 ? void 0 : failedMessage.sentAt) !== null && _c !== void 0 ? _c : new Date().toISOString(),
                    editedAt: null,
                    isSystem: false,
                    author: author,
                    deliveryStatus: ChatMessageDeliveryStatus.Sending,
                });
            }
            actions.setLoading(true);
            return publishChatMessageApi({ chatId: chatId, textMessage: textMessage })
                .then(function (message) {
                if (useChatMessagesStore.getState().chatId !== chatId)
                    return;
                actions.confirmLocalMessage(localId, message);
                actions.clearNewMessages();
                useChatsListStore.getState().updateChatLastMessage({ chatId: chatId, message: message, incrementUnread: false });
                invalidateLatestChatMessages(chatId);
                if (!useChatsListStore.getState().content.some(function (_a) {
                    var id = _a.id;
                    return id === chatId;
                })) {
                    queryClient.invalidateQueries({ queryKey: chatsListQueryKeys.all });
                }
                successCb === null || successCb === void 0 ? void 0 : successCb();
            })
                .catch(function (error) {
                console.log('publishChatMessageAction ->', error);
                if (useChatMessagesStore.getState().chatId !== chatId)
                    return;
                actions.failLocalMessage(localId);
                errorCb === null || errorCb === void 0 ? void 0 : errorCb();
            });
        }, editChatMessageAction: function (_a) {
            var chatId = _a.chatId, messageId = _a.messageId, newTextMessage = _a.newTextMessage, successCb = _a.successCb, errorCb = _a.errorCb;
            actions.setLoading(true);
            return editChatMessageApi({ chatId: chatId, messageId: messageId, newTextMessage: newTextMessage })
                .then(function (message) {
                if (useChatMessagesStore.getState().chatId !== chatId)
                    return;
                actions.editMessage(message);
                useChatsListStore.getState().editChatLastMessage({ chatId: chatId, message: message });
                invalidateLatestChatMessages(chatId);
                successCb === null || successCb === void 0 ? void 0 : successCb();
            })
                .catch(function (error) {
                console.log('editChatMessageAction ->', error);
                if (useChatMessagesStore.getState().chatId !== chatId)
                    return;
                actions.setLoading(false);
                errorCb === null || errorCb === void 0 ? void 0 : errorCb();
            });
        }, markChatMessagesAsReadAction: function (_a) {
            var chatId = _a.chatId, readAt = _a.readAt, successCb = _a.successCb, errorCb = _a.errorCb;
            return markChatMessagesAsReadApi({ chatId: chatId, readAt: readAt })
                .then(function () {
                successCb === null || successCb === void 0 ? void 0 : successCb();
            })
                .catch(function (error) {
                console.log('markChatMessagesAsReadAction ->', error);
                actions.setLoading(false);
                errorCb === null || errorCb === void 0 ? void 0 : errorCb();
            });
        }, deleteChatMessageAction: function (_a) {
            var chatId = _a.chatId, messageId = _a.messageId, successCb = _a.successCb, errorCb = _a.errorCb;
            return deleteChatMessageApi({ chatId: chatId, messageId: messageId })
                .then(function () {
                actions.removeMessage(messageId);
                invalidateLatestChatMessages(chatId);
                if (useChatsListStore
                    .getState()
                    .content.some(function (chat) { var _a; return chat.id === chatId && ((_a = chat.lastMessage) === null || _a === void 0 ? void 0 : _a.id) === messageId; })) {
                    queryClient.invalidateQueries({ queryKey: chatsListQueryKeys.all });
                }
                successCb === null || successCb === void 0 ? void 0 : successCb();
            })
                .catch(function (error) {
                console.log('deleteChatMessageAction ->', error);
                actions.setLoading(false);
                errorCb === null || errorCb === void 0 ? void 0 : errorCb();
            });
        } })); }, [actions, invalidateLatestChatMessages, queryClient]);
};
